import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Mail, Phone, Ruler, Check } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { ContactForm } from "../components/ContactForm";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { SearchTrigger } from "../components/GlobalSearch";
import { imageUrl } from "../config";
import { COMPANY } from "../data";
import { useLang } from "../i18n";

const IMG_FROMAGER = "/api/files/veneer-export/products/28541b8a-9616-4474-9eb2-4fa7d79e5cb9.png";
const IMG_LLOYDS = "/api/files/veneer-export/products/9078979c-5777-4a40-afac-c14d2007bc47.png";
const IMG_ALBASIA = "/api/files/veneer-export/products/4d7a43de-189c-4ae2-a983-cb6211b73178.jpg";
const IMG_OKOUME = "/api/files/veneer-export/products/fa2063bc-854b-4de4-bd36-0d42273bb0be.png";

const HERO = "/api/files/veneer-export/products/5cbe23be-c7e8-4589-a5a6-9bf2c98b4931.jpg";

const TYPES = [
  {
    image: IMG_FROMAGER,
    tr: {
      name: "Fromager Kontrplak",
      tagline: "Fuma (Ceiba) ağacından üretilen, hafif ve kolay bükülen esnek kontrplak.",
      tags: ["Esnek", "Ultra Hafif"],
      olcu: "1220 × 2440 mm · 4–24 mm",
      features: [
        { title: "Esneklik", desc: "Kavisli ve oval tasarımlar için idealdir." },
        { title: "Ultra Hafif", desc: "Çok düşük yoğunluğu ile ağırlık istenmeyen projelere idealdir." },
      ],
      kullanim: ["İç mekân dekorasyonu", "Kolon kaplama", "Kavisli mobilya", "Tekne / yat iç dizaynı"],
    },
    en: {
      name: "Fromager Plywood",
      tagline: "Flexible plywood made from Fuma (Ceiba) wood — lightweight and easy to bend.",
      tags: ["Flexible", "Ultra Light"],
      olcu: "1220 × 2440 mm · 4–24 mm",
      features: [
        { title: "Flexibility", desc: "Ideal for curved and oval designs." },
        { title: "Ultra Light", desc: "With its very low density, ideal for projects where weight matters." },
      ],
      kullanim: ["Interior decoration", "Column cladding", "Curved furniture", "Boat / yacht interior design"],
    },
  },
  {
    image: IMG_LLOYDS,
    tr: {
      name: "Lloyds Onaylı Fromager Kontrplak",
      tagline: "Uluslararası denizcilik standardında (BS 1088), suya dayanıklı WBP tutkallı marin kontrplak.",
      tags: ["Marine", "WBP", "Lloyd's Onaylı", "BS 1088"],
      olcu: "1220 × 2440 mm · EN 314-2 Sınıf 3 (WBP)",
      features: [
        { title: "Lloyd's Onaylı", desc: "Lloyd's Register tarafından tescillidir." },
        { title: "Su Direnci", desc: "EN 314-2 Sınıf 3 (WBP) — nem ve suya dayanıklıdır." },
        { title: "Sağlam & Ultra Hafif", desc: "Fuma katmanlarıyla çok hafif olmasına rağmen iyi bir mukavemet oranı sunar." },
      ],
      kullanim: ["Tekne, yat ve gemi inşaatı", "Deniz aracı iç mobilyaları", "Yüksek nem ve dış ortam uygulamaları"],
    },
    en: {
      name: "Lloyd's Approved Fromager Plywood",
      tagline: "Marine plywood with water-resistant WBP glue, built to the international marine standard (BS 1088).",
      tags: ["Marine", "WBP", "Lloyd's Approved", "BS 1088"],
      olcu: "1220 × 2440 mm · EN 314-2 Class 3 (WBP)",
      features: [
        { title: "Lloyd's Approved", desc: "Certified by Lloyd's Register." },
        { title: "Water Resistance", desc: "EN 314-2 Class 3 (WBP) — resistant to moisture and water." },
        { title: "Strong & Ultra Light", desc: "Despite being very light thanks to Fuma layers, it offers a good strength ratio." },
      ],
      kullanim: ["Boat, yacht and ship building", "Marine vessel interior furniture", "High-humidity and outdoor applications"],
    },
  },
  {
    image: IMG_ALBASIA,
    tr: {
      name: "Albasia Kontrplak",
      tagline: "Albasia Falcata (sengon) ağacından, olağanüstü hafif ve kolay işlenen kontrplak.",
      tags: ["Hafif", "Kolay İşlenir"],
      olcu: "1220 × 2500 mm · 3–18 mm",
      features: [
        { title: "Hafif", desc: "Standart levhalara göre belirgin şekilde hafiftir." },
        { title: "Kolay İşlenir", desc: "Kesim, montaj ve CNC işlemleri pratiktir." },
      ],
      kullanim: ["Karavan yapımı", "Hafif mobilya, raf ve panel", "Tekne içi montaj"],
    },
    en: {
      name: "Albasia Plywood",
      tagline: "Extraordinarily light and easy-to-work plywood from Albasia Falcata (sengon) wood.",
      tags: ["Light", "Easy to Work"],
      olcu: "1220 × 2500 mm · 3–18 mm",
      features: [
        { title: "Light", desc: "Significantly lighter than standard panels." },
        { title: "Easy to Work", desc: "Cutting, assembly and CNC operations are practical." },
      ],
      kullanim: ["Caravan construction", "Lightweight furniture, shelves and panels", "Boat interior fitting"],
    },
  },
  {
    image: IMG_OKOUME,
    tr: {
      name: "Okoume (Oküme) Kontrplak",
      tagline: "Gabon okoume ağacından, hafif, suya dayanıklı ve pürüzsüz yüzeyli marin kontrplak.",
      tags: ["Marine", "Hafif", "Pürüzsüz Yüzey"],
      olcu: "1220 × 2440 mm · 4–18 mm",
      features: [
        { title: "Hafiflik", desc: "Düşük yoğunluğu ile oldukça hafiftir." },
        { title: "Su Direnci", desc: "WBP tutkallı; marin sınıfında kullanılır." },
        { title: "Pürüzsüz Yüzey", desc: "Düzgün, estetik yüzey; iyi vida tutar." },
      ],
      kullanim: ["Tekne ve yat yapımı", "Karavan içi dizayn", "Mobilya ve dekorasyon"],
    },
    en: {
      name: "Okoume Plywood",
      tagline: "Marine plywood from Gabon okoume wood — light, water resistant, with a smooth surface.",
      tags: ["Marine", "Light", "Smooth Surface"],
      olcu: "1220 × 2440 mm · 4–18 mm",
      features: [
        { title: "Lightness", desc: "Quite light thanks to its low density." },
        { title: "Water Resistance", desc: "WBP glued; used in marine grade." },
        { title: "Smooth Surface", desc: "A clean, aesthetic surface with good screw holding." },
      ],
      kullanim: ["Boat and yacht building", "Caravan interior design", "Furniture and decoration"],
    },
  },
];

const COMPARE = [
  { high: false, tr: { name: "Fromager", agirlik: "Ultra Hafif", su: "Standart", olcu: "1220 × 2440 mm · 4–24 mm" }, en: { name: "Fromager", agirlik: "Ultra Light", su: "Standard", olcu: "1220 × 2440 mm · 4–24 mm" } },
  { high: true, tr: { name: "Lloyds Onaylı Fromager", agirlik: "Ultra Hafif", su: "Yüksek (Marine / WBP)", olcu: "1220 × 2440 mm · Sınıf 3" }, en: { name: "Lloyd's Approved Fromager", agirlik: "Ultra Light", su: "High (Marine / WBP)", olcu: "1220 × 2440 mm · Class 3" } },
  { high: false, tr: { name: "Albasia", agirlik: "Hafif", su: "Standart", olcu: "1220 × 2500 mm · 3–18 mm" }, en: { name: "Albasia", agirlik: "Light", su: "Standard", olcu: "1220 × 2500 mm · 3–18 mm" } },
  { high: true, tr: { name: "Okoume", agirlik: "Hafif", su: "Yüksek (Marine / WBP)", olcu: "1220 × 2440 mm · 4–18 mm" }, en: { name: "Okoume", agirlik: "Light", su: "High (Marine / WBP)", olcu: "1220 × 2440 mm · 4–18 mm" } },
];

const QuoteModal = ({ product, onClose }) => {
  const [waOpen, setWaOpen] = useState(false);
  const { t } = useLang();
  const waMessage = t("lightbox.waMessage", product);
  const waLink = (num) => `https://wa.me/${num.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waMessage)}`;
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  return (
    <motion.div
      data-testid="kontrplak-quote-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[#0D0C0B]/95 backdrop-blur-xl" />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-lg bg-[#F5F2EE] ring-1 ring-black/10 rounded-lg p-8 md:p-10 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button data-testid="kontrplak-quote-close" onClick={onClose} aria-label={t("common.close")}
          className="absolute top-4 right-4 w-10 h-10 rounded-full border border-black/15 flex items-center justify-center text-[#1A1715] hover:bg-[#7BA05B] hover:border-[#7BA05B] hover:text-[#0D0C0B] transition-all duration-300">
          <X size={16} strokeWidth={1.5} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-[#7BA05B]" />
          <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#C67D57]">{t("lightbox.quote")}</span>
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-[#1A1715] tracking-tight leading-tight mb-2">{product}</h3>
        <p className="text-[#6B6560] text-sm mb-6">{t("lightbox.quoteSubPlywood")}</p>
        <div className="mb-6">
          {!waOpen ? (
            <button type="button" data-testid="kontrplak-wa-toggle" onClick={() => setWaOpen(true)}
              className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] text-[#0D0C0B] font-sans uppercase tracking-[0.15em] text-[11px] py-4 rounded-full hover:bg-[#1EBE5B] transition-all duration-300">
              <WhatsAppIcon size={17} /> {t("lightbox.waButton")}
            </button>
          ) : (
            <div data-testid="kontrplak-wa-numbers">
              <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560] mb-2">{t("lightbox.waSelect")}</p>
              <div className="grid grid-cols-2 gap-2">
                {COMPANY.whatsapp.map((p, i) => (
                  <a key={i} href={waLink(p)} target="_blank" rel="noopener noreferrer" data-testid={`kontrplak-wa-${i}`}
                    className="flex items-center gap-2 bg-[#25D366]/10 border border-[#25D366]/40 text-[#1A1715] text-xs py-2.5 px-3 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300">
                    <WhatsAppIcon size={14} className="text-[#25D366] shrink-0" /> {p}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 mb-6">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560]">{t("lightbox.orForm")}</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <ContactForm initialMessage={t("lightbox.quotePrefill", product)} onSubmitted={onClose} />
        <div className="mt-8 pt-6 border-t border-black/10 space-y-4">
          <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#6B6560]">{t("lightbox.direct")}</p>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-[#1A1715] hover:text-[#C67D57] transition-colors">
            <Mail size={16} strokeWidth={1.5} className="text-[#A65D37]" /><span className="text-sm">{COMPANY.email}</span>
          </a>
          <div className="grid grid-cols-2 gap-2">
            {COMPANY.phone.map((p, i) => (
              <a key={i} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 border border-[#A65D37]/40 text-[#1A1715] text-xs py-2.5 px-3 rounded-lg hover:bg-[#A65D37]/15 transition-all duration-300">
                <Phone size={14} strokeWidth={1.5} className="text-[#A65D37] shrink-0" /> {p}
              </a>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function KontrplakPage() {
  const [quote, setQuote] = useState(null);
  const { lang, t } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="kontrplak-page" className="bg-white min-h-screen">
      <Navigation />

      {/* Hero */}
      <section className="relative h-[42vh] min-h-[340px] overflow-hidden">
        <img src={imageUrl(HERO)} alt={t("kontrplak.heroTitle")} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B] via-[#0D0C0B]/30 to-[#0D0C0B]/40" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="font-sans uppercase tracking-[0.3em] text-xs text-[#7BA05B] mb-5">{t("kontrplak.heroSub")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-5xl md:text-7xl tracking-tighter text-[#F2EFEB] leading-none">{t("kontrplak.heroTitle")}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="text-[#E5E1DB] text-sm md:text-base mt-5 max-w-xl">{t("kontrplak.heroDesc")}</motion.p>
        </div>
      </section>

      {/* Karşılaştırma Tablosu */}
      <section data-testid="kontrplak-compare" className="max-w-6xl mx-auto px-6 md:px-12 pt-16 md:pt-24">
        <SearchTrigger light className="mb-14" />
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-[#7BA05B]" />
          <span className="font-sans uppercase tracking-[0.25em] text-[10px] text-[#2F4A26]">{t("kontrplak.compareLabel")}</span>
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-[#1A1715] tracking-tight mb-8">{t("kontrplak.compareHeading")}</h2>

        {/* Masaüstü tablo */}
        <div className="hidden md:block overflow-hidden rounded-lg ring-1 ring-black/10">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F0EDE8]">
                <th className="font-sans uppercase tracking-[0.15em] text-[10px] text-[#6B6560] px-6 py-4">{t("kontrplak.thType")}</th>
                <th className="font-sans uppercase tracking-[0.15em] text-[10px] text-[#6B6560] px-6 py-4">{t("kontrplak.thWeight")}</th>
                <th className="font-sans uppercase tracking-[0.15em] text-[10px] text-[#6B6560] px-6 py-4">{t("kontrplak.thWater")}</th>
                <th className="font-sans uppercase tracking-[0.15em] text-[10px] text-[#6B6560] px-6 py-4">{t("kontrplak.thSize")}</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row) => {
                const c = row[lang] || row.tr;
                return (
                  <tr key={c.name} className="border-t border-black/10 hover:bg-black/[0.03] transition-colors">
                    <td className="px-6 py-4 text-[#1A1715] text-sm font-medium">{c.name}</td>
                    <td className="px-6 py-4 text-[#6B6560] text-sm">{c.agirlik}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={row.high ? "text-[#7BA05B]" : "text-[#B7B1A9]"}>{c.su}</span>
                    </td>
                    <td className="px-6 py-4 text-[#B7B1A9] text-sm">{c.olcu}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobil kart görünümü */}
        <div className="md:hidden space-y-3">
          {COMPARE.map((row) => {
            const c = row[lang] || row.tr;
            return (
              <div key={c.name} className="rounded-lg ring-1 ring-black/10 bg-[#F5F2EE] p-5">
                <p className="text-[#1A1715] text-sm font-medium mb-3">{c.name}</p>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  <div className="flex justify-between"><span className="text-[#6B6560]">{t("kontrplak.thWeight")}</span><span className="text-[#6B6560]">{c.agirlik}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B6560]">{t("kontrplak.thWater")}</span><span className={row.high ? "text-[#7BA05B]" : "text-[#B7B1A9]"}>{c.su}</span></div>
                  <div className="flex justify-between"><span className="text-[#6B6560]">{t("kontrplak.size")}</span><span className="text-[#B7B1A9]">{c.olcu}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Types */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-16 md:space-y-24">
        {TYPES.map((type, i) => {
          const d = type[lang] || type.tr;
          return (
            <motion.div key={d.name} data-testid={`kontrplak-type-${i}`}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center bg-white ring-1 ring-black/20 shadow-[0_4px_24px_rgba(0,0,0,0.08)] rounded-xl p-6 md:p-10 ${i % 2 ? "md:[direction:rtl]" : ""}`}>
              <div className="relative overflow-hidden rounded-lg aspect-[5/4] bg-[#F0EDE8] ring-1 ring-black/10 [direction:ltr]">
                <img src={imageUrl(type.image)} alt={d.name} loading="lazy" decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/25 to-transparent" />
                <span className="absolute top-5 left-5 font-serif text-5xl text-white/70 leading-none">0{i + 1}</span>
              </div>
              <div className="[direction:ltr]">
                <h2 className="font-serif text-3xl md:text-4xl text-[#1A1715] tracking-tight leading-tight">{d.name}</h2>
                <p className="text-[#6B6560] mt-4 text-sm md:text-base leading-relaxed">{d.tagline}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {d.tags.map((tag) => (
                    <span key={tag} className="font-sans uppercase tracking-[0.15em] text-[10px] text-[#2F4A26] bg-[#7BA05B]/10 border border-[#2F4A26]/25 rounded-full px-3 py-1.5">{tag}</span>
                  ))}
                </div>

                <div className="mt-7">
                  <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mb-3">{t("kontrplak.features")}</p>
                  <ul className="space-y-2.5">
                    {d.features.map((f) => (
                      <li key={f.title} className="flex items-start gap-2.5 text-sm leading-relaxed">
                        <Check size={15} strokeWidth={2} className="text-[#7BA05B] mt-0.5 shrink-0" />
                        <span className="text-[#6B6560]"><span className="text-[#1A1715] font-medium">{f.title}:</span> {f.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6">
                  <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mb-3">{t("kontrplak.uses")}</p>
                  <ul className="space-y-2">
                    {d.kullanim.map((k) => (
                      <li key={k} className="flex items-start gap-2.5 text-sm text-[#6B6560] leading-relaxed">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#7BA05B] mt-1.5 shrink-0" />
                        <span>{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 flex items-start gap-2.5 text-sm text-[#6B6560] border-t border-black/10 pt-5">
                  <Ruler size={15} strokeWidth={1.6} className="text-[#2F4A26] mt-0.5 shrink-0" />
                  <span>{d.olcu}</span>
                </div>

                <button type="button" data-testid={`kontrplak-quote-btn-${i}`} onClick={() => setQuote(d.name)}
                  className="group mt-7 inline-flex items-center gap-2.5 bg-[#2F4A26] text-white font-sans uppercase tracking-[0.15em] text-[11px] px-7 py-3.5 rounded-full hover:bg-[#3B5A31] transition-all duration-300">
                  {t("lightbox.quote")} <ArrowRight size={15} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </section>

      <AnimatePresence>
        {quote && <QuoteModal product={quote} onClose={() => setQuote(null)} />}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
